/**
 * AddToCart — click delegation for `[data-action=add-to-cart]` buttons.
 * Performs `POST /cart/add.js`, dispatches `product:added` on success,
 * shows inline error UI on failure.
 *
 * Extracted from global.js. Used by (at least) ProductForm, CartDrawer,
 * CarouselTest, ProductElement, collection-filters-form.js.
 *
 * Loaded as classic defer script from `layout/theme.liquid` AFTER
 * `delegate.js` (needs `Delegate`) and BEFORE `global.js` (so existing
 * `new AddToCart(...)` bare-name calls in global.js classes resolve).
 * Attaches `window.AddToCart` for feature modules.
 *
 * Depends on:
 *   - Delegate (delegate.js)
 *   - Form (still in global.js — resolves at method-call time)
 *   - window.routes (theme.liquid inline)
 *   - window.theme (theme.liquid inline)
 *
 * Phase 3 migration will convert to `export class AddToCart` + importmap.
 */

import { Delegate } from '@theme/delegate';
import { Form } from '@theme/form';

/**
 * @param {string|number} variantId
 * @param {number} [quantity]
 * @param {{ dispatchTarget?: EventTarget }} [options]
 */
export async function addVariantToCart(variantId, quantity = 1, options = {}) {
  const response = await fetch(`${window.routes.localeRootUrl}/cart/add.js`, {
    body: JSON.stringify({ id: Number(variantId), quantity }),
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    const content = await response.json().catch(() => null);
    throw new Error(content?.description || 'Add to cart failed');
  }

  if (window.theme.cartMode === 'page') {
    window.location.href = window.routes.cart_page_url;
    return;
  }

  if (window.location.href.includes('cart')) {
    window.location.reload();
    return;
  }

  const dispatchTarget = options.dispatchTarget || document.body;

  dispatchTarget.dispatchEvent(
    new CustomEvent('product:added', {
      bubbles: true,
      detail: {
        variant: variantId,
        quantity,
      },
    }),
  );
}

export class AddToCart {
  constructor(element) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.pageLoader = document.body.querySelector('.page-loader');
    this.bindEvents();
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action=add-to-cart]', this.addToCart.bind(this));
  }

  addToCart(event) {
    var _this = this;
    event.preventDefault();
    var addToCartButton = event.target;
    // No page loader — the cart drawer opens instantly and reconciles in the
    // background; the disabled button is the only in-flight feedback needed.
    addToCartButton.setAttribute('disabled', 'disabled');
    var formElement = event.target.closest('form');
    formElement.querySelector('[name="id"]').removeAttribute('disabled');
    var formHelper = new Form();
    const serialized = formHelper.serialize(formElement);
    fetch(window.routes.localeRootUrl + '/cart/add.js', {
      body: JSON.stringify(serialized),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then(function (response) {
      if (response.ok) {
        if (window.theme.cartMode === 'page') {
          location.href = window.routes.cart_page_url;
        }
        if (window.location.href.indexOf('cart') != -1) {
          location.reload();
          return;
        }
        addToCartButton.removeAttribute('disabled');
        _this.element.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: serialized.id,
              quantity: serialized.quantity,
            },
          })
        );
      } else {
        response.json().then(function (content) {
          if (_this.element.querySelector('.ProductForm--Error')) {
            _this.element.querySelector('.ProductForm--Error').classList.add('visible');
            setTimeout(function () {
              _this.element.querySelector('.ProductForm--Error').classList.remove('visible');
            }, 2500);
            _this.pageLoader.style.left = 'unset';
            _this.pageLoader.style.right = 0 + 'px';
            _this.pageLoader.style.width = 0 + '%';
            setTimeout(() => {
              _this.pageLoader.style.left = 0 + 'px';
              _this.pageLoader.style.right = 'unset';
            }, 550);
          } else {
            var errorMessageElement = document.createElement('span');
            errorMessageElement.className = 'u-p4 accent';
            errorMessageElement.innerHTML = content['description'];
            addToCartButton.removeAttribute('disabled');
            addToCartButton.insertAdjacentElement('afterend', errorMessageElement);
            setTimeout(function () {
              errorMessageElement.remove();
            }, 2500);
            _this.pageLoader.style.left = 'unset';
            _this.pageLoader.style.right = 0 + 'px';
            _this.pageLoader.style.width = 0 + '%';
            setTimeout(() => {
              _this.pageLoader.style.left = 0 + 'px';
              _this.pageLoader.style.right = 'unset';
            }, 550);
          }
        });
      }
    });
  }
}

// Bridge for classic-script consumers (global.js). Removed in Faza 5.
window.AddToCart = AddToCart;
